import {Injectable} from "@angular/core";
import {TeacherService} from "./teacher.service";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import {AnnualSchoolProfile} from "../interfaces/annual-school-profile";
import {Teacher} from "../interfaces/teacher";

@Injectable({
  providedIn: "root"
})
export class AnnualSchoolProfileService{

  private db = firebase.firestore();
  private projectMap: Record<string, string> = {};

  constructor(private teacherService: TeacherService) {}

  /** Get the current logged-in teacher */
  async getCurrentTeacher(): Promise<Teacher | null> {
    return await this.teacherService.getTeacher();
  }

  /** Load all projects into a map with the id as the title */
  private async loadProjects() {
    const snapshot = await this.db.collection('projects').get();
    snapshot.forEach(doc => {
      const data = doc.data() as any;
      if (data?.title) this.projectMap[doc.id] = data.title;
    });
  }

  /** Fetch Annual School Profiles based on role */
  async getProfiles(): Promise<AnnualSchoolProfile[]> {
    await this.loadProjects();

    const teacher = await this.getCurrentTeacher();
    const role = teacher?.role?.toLowerCase() ?? '';
    const email = teacher?.email;
    const school = teacher?.school;

    const snapshot = await this.db.collection('annualSchoolProfile').get();
    const profiles: AnnualSchoolProfile[] = [];

    snapshot.forEach(doc => {
      const data = { id: doc.id, ...doc.data() } as AnnualSchoolProfile;

      // Role-based filtering
      if(role === 'science directors') return;
      if (role === 'teacher' && email !== data.ghsRepresentativeEmail) return;
      if (role === 'ghs teacher representative' && school != data.school) return;
      if(role !== 'admin' && data.isDeleted) return;

      // Turn project references into the actual project names
      const projectRefs: firebase.firestore.DocumentReference[] = Array.isArray(data.project) ? data.project : [];
      (data as any).projectNames = projectRefs
        .map(ref => (ref as any).path?.split('/').pop())
        .map(id => id ? this.projectMap[id] ?? '' : '')
        .filter(name => !!name);

      profiles.push(data);
    });

    return profiles.sort((a, b) => {
      const yearA = parseInt(a.schoolYear.split('-')[0], 10);
      const yearB = parseInt(b.schoolYear.split('-')[0], 10);
      if (yearA !== yearB) return yearB - yearA;
      const schoolDiff = a.school.localeCompare(b.school);
      if (schoolDiff !== 0) return schoolDiff;
      return a.ghsRepresentativeName.localeCompare(b.ghsRepresentativeName);
    });
  }

  /** Fetch ALL Annual School Profiles*/
  async getAllProfiles(): Promise<AnnualSchoolProfile[]> {
    await this.loadProjects();
    const snapshot = await this.db.collection('annualSchoolProfile').get();
    const profiles: AnnualSchoolProfile[] = [];

    snapshot.forEach(doc => {
      const data = { id: doc.id, ...doc.data() } as AnnualSchoolProfile;
      // Turn project references into the actual project names
      const projectRefs: firebase.firestore.DocumentReference[] = Array.isArray(data.project) ? data.project : [];
      (data as any).projectNames = projectRefs
        .map(ref => (ref as any).path?.split('/').pop())
        .map(id => id ? this.projectMap[id] ?? '' : '')
        .filter(name => !!name);

      profiles.push(data);
    });

    return profiles.sort((a, b) => {
      const yearA = parseInt(a.schoolYear.split('-')[0], 10);
      const yearB = parseInt(b.schoolYear.split('-')[0], 10);
      if (yearA !== yearB) return yearB - yearA;
      const schoolDiff = a.school.localeCompare(b.school);
      if (schoolDiff !== 0) return schoolDiff;
      return a.ghsRepresentativeName.localeCompare(b.ghsRepresentativeName);
    });
  }
}
